import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { Localization } from "@viamap/viamap2-common";
import { GlassButton } from "src/components/GlassButtons";
import { validatePLAExpression } from "./PLAExpression";
import { applyPLAExpressionCompletion, getPLAExpressionCompletions, PLAExpressionCompletion } from "./PLAExpressionCompletion";
import { PLAElementRecord, PLAProject } from "./PLAState";

export function PLAExpressionInput(props: { value: string; onChange: (expression: string) => void; project: PLAProject; elementTypeId: string; elements: PLAElementRecord[] }) {
   const input = useRef<HTMLTextAreaElement>(null);
   const list = useRef<HTMLDivElement>(null);
   const id = useId();
   const [cursor, setCursor] = useState(props.value.length);
   const [open, setOpen] = useState(false);
   const [selected, setSelected] = useState(0);
   const validation = useMemo(() => validatePLAExpression(props.value, { project: props.project, elementTypeId: props.elementTypeId }), [props.value, props.project, props.elementTypeId]);
   const suggestions = useMemo(() => getPLAExpressionCompletions(props.value, cursor, props.project, props.elementTypeId, props.elements), [props.value, cursor, props.project, props.elementTypeId, props.elements]);
   const active = Math.min(selected, Math.max(0, suggestions.length - 1));
   const visible = open && suggestions.length > 0;
   useEffect(() => {
      const container = list.current;
      const option = container?.children[active];
      if (!container || !(option instanceof HTMLElement)) return;
      if (option.offsetTop < container.scrollTop) container.scrollTop = option.offsetTop;
      else if (option.offsetTop + option.offsetHeight > container.scrollTop + container.clientHeight) container.scrollTop = option.offsetTop + option.offsetHeight - container.clientHeight;
   }, [active, visible]);

   const insert = (completion: PLAExpressionCompletion) => {
      const next = applyPLAExpressionCompletion(props.value, completion);
      props.onChange(next.expression);
      setCursor(next.cursor);
      setSelected(0);
      setOpen(false);
      requestAnimationFrame(() => {
         input.current?.focus();
         input.current?.setSelectionRange(next.cursor, next.cursor);
      });
   };
   const showSuggestions = () => {
      setCursor(input.current?.selectionStart ?? props.value.length);
      setSelected(0);
      setOpen(true);
      input.current?.focus();
   };
   const onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.ctrlKey && event.code === "Space") {
         event.preventDefault();
         event.stopPropagation();
         showSuggestions();
      } else if (visible && ["ArrowDown", "ArrowUp", "Enter", "Tab", "Escape"].includes(event.key)) {
         event.preventDefault();
         event.stopPropagation();
         if (event.key === "Escape") setOpen(false);
         else if (event.key === "ArrowDown") setSelected((active + 1) % suggestions.length);
         else if (event.key === "ArrowUp") setSelected((active + suggestions.length - 1) % suggestions.length);
         else insert(suggestions[active]);
      } else if (event.key === "Enter") {
         event.stopPropagation();
      }
   };
   return <div>
      <label htmlFor={id}>{Localization.getText("Expression")}</label>
      <textarea
         id={id}
         ref={input}
         rows={5}
         value={props.value}
         spellCheck={false}
         aria-invalid={props.value.trim() ? !validation.valid : undefined}
         aria-describedby={`${id}-validation ${id}-hint`}
         aria-autocomplete="list"
         aria-controls={visible ? `${id}-suggestions` : undefined}
         aria-activedescendant={visible ? `${id}-suggestion-${active}` : undefined}
         onKeyDown={onKeyDown}
         onSelect={(event) => setCursor(event.currentTarget.selectionStart)}
         onBlur={() => setOpen(false)}
         onChange={(event) => {
            props.onChange(event.target.value);
            setCursor(event.target.selectionStart);
            setSelected(0);
            setOpen(event.target.selectionStart === event.target.selectionEnd);
         }}
         style={{ display: "block", width: "100%", minWidth: 180, maxWidth: "100%", minHeight: 80, resize: "both", fontFamily: "monospace" }}
      />
      <div id={`${id}-validation`} role="status" aria-live="polite" style={{ whiteSpace: "pre-wrap", marginTop: 4, color: !props.value.trim() ? undefined : !validation.valid ? "#e74c3c" : validation.warnings?.length ? "#b36b00" : undefined }}>
         {!props.value.trim() ? Localization.getText("Expression is required") : !validation.valid ? validation.error : validation.warnings?.length ? validation.warnings.join("\n") : Localization.getText("Expression syntax valid")}
      </div>
      <div style={{ marginTop: 8 }}>
         <GlassButton onMouseDown={(event) => event.preventDefault()} onClick={showSuggestions}>{Localization.getText("Expression suggestions")}</GlassButton>
         <p id={`${id}-hint`}>{Localization.getText("Expression completion hint")}</p>
      </div>
      {visible && <div ref={list} id={`${id}-suggestions`} role="listbox" aria-label={Localization.getText("Expression suggestions")} style={{ position: "relative", maxHeight: 180, overflow: "auto", border: "1px solid currentColor", marginBottom: 8 }}>
         {suggestions.map((suggestion, index) => <div key={`${suggestion.kind}:${suggestion.label}`} id={`${id}-suggestion-${index}`} role="option" aria-selected={index === active} onMouseDown={(event) => { event.preventDefault(); insert(suggestion); }} style={{ padding: "4px 8px", cursor: "pointer", background: index === active ? "rgba(45,143,133,0.25)" : undefined }}>
            <code>{suggestion.label}</code> <small>{Localization.getText(`Expression completion ${suggestion.kind}`)}{suggestion.attributeType ? ` · ${Localization.getText(`Expression completion attribute ${suggestion.attributeType}`)}` : ""}{suggestion.valueType ? ` · ${suggestion.valueType}` : ""}</small>
         </div>)}
      </div>}
   </div>;
}
